// src/pages/ELearning/CalendarPage.tsx — ФИЧА N: Календарь назначений
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAssignmentsCalendar } from '../../api/client';
import { Loader2, Calendar as CalendarIcon, ChevronLeft, ChevronRight, Clock, User, Award } from 'lucide-react';

const MONTHS = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
const DAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

const CalendarPage: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const from = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).toISOString();
        const to = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0, 23, 59).toISOString();
        const d = await getAssignmentsCalendar({ dateFrom: from, dateTo: to });
        setData(d);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [currentMonth]);

  const prevMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  const nextMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));

  // Генерируем сетку месяца
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startWeekday = (firstDay.getDay() + 6) % 7; // 0=Пн
  const daysInMonth = lastDay.getDate();

  const cells: Array<{ day: number | null; dateStr: string | null }> = [];
  for (let i = 0; i < startWeekday; i++) cells.push({ day: null, dateStr: null });
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    cells.push({ day: d, dateStr });
  }
  while (cells.length % 7 !== 0) cells.push({ day: null, dateStr: null });

  const byDate = data?.byDate || {};
  const today = new Date().toISOString().slice(0, 10);
  const selectedItems = selectedDay ? (byDate[selectedDay] || []) : [];

  return (
    <>
      <style>{`
        @keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        .cal-hero { animation: fadeUp 0.5s ease-out; }
        .cal-cell { transition: all 0.2s ease; }
        .cal-cell:hover { transform: scale(1.02); }
      `}</style>

      <div className="cal-hero" style={styles.hero}>
        <div style={styles.heroIcon}><CalendarIcon size={28} color="#fff" /></div>
        <div style={{ flex: 1 }}>
          <h1 style={styles.title}>Календарь назначений</h1>
          <p style={styles.subtitle}>Кто, что и когда должен пройти</p>
        </div>
      </div>

      {/* Навигация по месяцам */}
      <div style={styles.monthNav}>
        <button onClick={prevMonth} style={styles.monthBtn}><ChevronLeft size={18} /></button>
        <div style={styles.monthTitle}>{MONTHS[month]} {year}</div>
        <button onClick={nextMonth} style={styles.monthBtn}><ChevronRight size={18} /></button>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <Loader2 size={40} color="#667eea" style={{ animation: 'spin 1s linear infinite' }} />
        </div>
      ) : (
        <div style={styles.calendar}>
          {/* Дни недели */}
          {DAYS.map(d => (
            <div key={d} style={styles.weekday}>{d}</div>
          ))}
          {/* Сетка дней */}
          {cells.map((cell, i) => {
            if (!cell.day) return <div key={i} style={styles.emptyCell} />;
            const items = byDate[cell.dateStr!] || [];
            const isToday = cell.dateStr === today;
            const isSelected = cell.dateStr === selectedDay;
            const hasItems = items.length > 0;
            return (
              <div
                key={i}
                onClick={() => setSelectedDay(cell.dateStr)}
                className="cal-cell"
                style={{
                  ...styles.dayCell,
                  ...(isToday ? styles.todayCell : {}),
                  ...(isSelected ? styles.selectedCell : {}),
                  ...(hasItems ? styles.hasItemsCell : {}),
                }}
              >
                <div style={styles.dayNum}>{cell.day}</div>
                {hasItems && (
                  <div style={styles.dayBadge}>{items.length}</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Детали выбранного дня */}
      {selectedDay && selectedItems.length > 0 && (
        <div style={styles.details}>
          <h3 style={styles.detailsTitle}>
            📅 {new Date(selectedDay).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })} — {selectedItems.length} назначений
          </h3>
          {selectedItems.map((a: any, i: number) => (
            <div key={a._id || i} style={styles.item} onClick={() => navigate(`/app/elearning/tests/${a.test?._id}`)}>
              <div style={styles.itemIcon}>
                <Award size={16} color="#fff" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={styles.itemTitle}>{a.test?.title || 'Тест'}</div>
                <div style={styles.itemMeta}>
                  <User size={11} /> {a.user?.name || a.user?.email || '—'}
                </div>
              </div>
              <div style={{
                ...styles.statusBadge,
                background: a.status === 'overdue' ? 'rgba(239,68,68,0.15)' : 'rgba(102,126,234,0.15)',
                color: a.status === 'overdue' ? '#ef4444' : '#667eea',
              }}>
                {a.status === 'overdue' ? '⚠️ Просрочен' : '⏳ Ожидает'}
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedDay && selectedItems.length === 0 && (
        <div style={styles.empty}>
          <p style={{ color: '#8b8fa8', fontSize: 14 }}>Нет назначений в этот день</p>
        </div>
      )}
    </>
  );
};

const styles: Record<string, React.CSSProperties> = {
  hero: { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 },
  heroIcon: { width: 56, height: 56, borderRadius: 18, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 12px 32px rgba(102,126,234,0.4)', flexShrink: 0 },
  title: { fontSize: 28, fontWeight: 800, margin: 0, background: 'linear-gradient(135deg, #1a1a2e 0%, #667eea 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', letterSpacing: '-0.5px' },
  subtitle: { color: '#8b8fa8', margin: '4px 0 0', fontSize: 14, fontWeight: 500 },

  monthNav: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, background: 'rgba(255,255,255,0.6)', borderRadius: 16, padding: '10px 16px', boxShadow: '0 4px 16px rgba(31,38,135,0.06)' },
  monthBtn: { padding: 8, background: 'transparent', border: 'none', cursor: 'pointer', borderRadius: 10, color: '#667eea' },
  monthTitle: { fontSize: 18, fontWeight: 800, color: '#1a1a2e' },

  calendar: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6, marginBottom: 24 },
  weekday: { textAlign: 'center', padding: 8, fontSize: 11, fontWeight: 800, color: '#8b8fa8', textTransform: 'uppercase', letterSpacing: 0.5 },
  emptyCell: { minHeight: 70 },
  dayCell: { position: 'relative', minHeight: 70, borderRadius: 14, background: 'rgba(255,255,255,0.5)', border: '1px solid rgba(255,255,255,0.6)', cursor: 'pointer', padding: 10, boxShadow: '0 2px 8px rgba(31,38,135,0.03)' },
  todayCell: { border: '2px solid #667eea', background: 'rgba(102,126,234,0.08)' },
  selectedCell: { border: '2px solid #764ba2', background: 'rgba(118,75,162,0.12)', transform: 'scale(1.02)' },
  hasItemsCell: { background: 'rgba(17,153,142,0.06)' },
  dayNum: { fontSize: 14, fontWeight: 700, color: '#1a1a2e' },
  dayBadge: { position: 'absolute', top: 6, right: 6, minWidth: 22, height: 22, padding: '0 6px', borderRadius: 11, background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', color: '#fff', fontSize: 11, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(17,153,142,0.4)' },

  details: { background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(16px)', borderRadius: 20, padding: 20, boxShadow: '0 8px 32px rgba(31,38,135,0.08)', animation: 'fadeUp 0.4s ease-out' },
  detailsTitle: { fontSize: 15, fontWeight: 700, color: '#1a1a2e', margin: '0 0 16px' },
  item: { display: 'flex', gap: 12, alignItems: 'center', padding: '12px 14px', borderRadius: 14, background: 'rgba(255,255,255,0.6)', marginBottom: 8, cursor: 'pointer', transition: 'all 0.2s' },
  itemIcon: { width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  itemTitle: { fontSize: 14, fontWeight: 700, color: '#1a1a2e', marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  itemMeta: { fontSize: 11, color: '#8b8fa8', display: 'flex', alignItems: 'center', gap: 4 },
  statusBadge: { padding: '4px 10px', borderRadius: 10, fontSize: 11, fontWeight: 800, flexShrink: 0 },

  empty: { padding: 40, textAlign: 'center', background: 'rgba(255,255,255,0.5)', borderRadius: 20, border: '1px dashed rgba(102,126,234,0.3)' },
};

export default CalendarPage;
